import {Injectable} from '@angular/core';
import {MeasureTypeEnum} from '../../../../core/services/state/measure-type.enum';
import {PressureUnitEnum} from '../../../../core/services/state/pressure-unit.enum';
import {PressureScaleBoundaries} from './pressure-scale-boundaries';
import {PressureGraphConfig} from '../../../../core/services/config/pressure-graph-config';
import {PressureUnitUtils} from '../../../../core/utils/pressure-unit-utils';
import {PatientData} from '../data/patient-data';

@Injectable()
export class PressureScaleService {

  private _patientData!: PatientData;
  private _graphConfig!: PressureGraphConfig;
  private _forceLiveBehavior: boolean = false;
  private _boundaries: PressureScaleBoundaries = new PressureScaleBoundaries();

  constructor() {
  }

  public get boundariesWithMargin(): PressureScaleBoundaries {
    if (this.measureType === MeasureTypeEnum.INFUSION_TEST && !this._forceLiveBehavior) {
      return new PressureScaleBoundaries(
        this._graphConfig.pressureInterval.min.fromUnit(this.unit),
        this._graphConfig.pressureInterval.max.fromType(this.measureType).fromUnit(this.unit),
      );
    }
    const margin = this._graphConfig.pressureInterval.liveMargin;
    return new PressureScaleBoundaries(
      PressureUnitUtils.convertMmHgToUnit(this._boundaries.minPressure - margin, this.unit) > 0 ?
        PressureUnitUtils.convertMmHgToUnit(this._boundaries.minPressure - margin, this.unit) : 0,
      PressureUnitUtils.convertMmHgToUnit(this._boundaries.maxPressure + margin, this.unit),
    );
  }

  public get pressureLabelStepSize(): number {
    if (this.measureType === MeasureTypeEnum.INFUSION_TEST && !this._forceLiveBehavior) {
      return this._graphConfig.graduations.pressureStepSize.labels.fromUnit(this.unit);
    }
    const pressureAmplitude = Math.abs(
      this.boundariesWithMargin.minPressure
      - this.boundariesWithMargin.maxPressure,
    );
    const livePressureStepConfig = this._graphConfig.graduations.livePressureStepSize;
    for (const size of livePressureStepConfig.stepSizeRange) {
      if (pressureAmplitude / size < livePressureStepConfig.maxNumberOfLabels) {
        return size;
      }
    }
    return livePressureStepConfig.stepSizeRange[livePressureStepConfig.stepSizeRange.length - 1];
  }

  public get pressureLineStepSize(): number {
    if (this.measureType === MeasureTypeEnum.INFUSION_TEST && !this._forceLiveBehavior) {
      return this._graphConfig.graduations.pressureStepSize.lines.fromUnit(this.unit);
    }

    return this.pressureLabelStepSize * this._graphConfig.graduations.livePressureStepSize.lineRatio;
  }

  private get measureType(): MeasureTypeEnum {
    return this._patientData.measureType;
  }

  private get unit(): PressureUnitEnum {
    return this._patientData.pressureUnit;
  }

  public init(patientData: PatientData, graphConfig: PressureGraphConfig, forceLiveBehavior: boolean = false): void {
    this._patientData = patientData;
    this._graphConfig = graphConfig;
    this._forceLiveBehavior = forceLiveBehavior;
  }

  public updateBoundaryFromValue(value: number): void {
    this._boundaries.updateFromValue(value);
  }

  public reset(): void {
    if (this.measureType === MeasureTypeEnum.INFUSION_TEST && !this._forceLiveBehavior) {
      this._boundaries.minPressure = this._graphConfig.pressureInterval.min.fromUnit(this.unit);
      this._boundaries.maxPressure =
        this._graphConfig.pressureInterval.max.fromType(this.measureType).fromUnit(this.unit);
    } else {
      this._boundaries.minPressure = Number.MAX_SAFE_INTEGER;
      this._boundaries.maxPressure = Number.MIN_SAFE_INTEGER;
    }
  }
}
